import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { ArrowUpRight, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL = {
  name: "",
  company: "",
  email: "",
  phone: "",
  estimated_tariffs: "",
  message: "",
};

export default function LeadForm() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) =>
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const validate = () => {
    if (!form.name.trim()) return "Please share your name.";
    if (!form.email.trim()) return "Please share your email.";
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!emailOk) return "That email address doesn't look right.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/leads`, {
        ...form,
        source: "landing_page",
      });
      toast.success(
        data?.message ||
          "Thank you — we'll reach out within one business day."
      );
      setForm(INITIAL);
      setSubmitted(true);
    } catch (e2) {
      const msg =
        e2?.response?.data?.detail ||
        "Something went wrong. Please try again or email us directly.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="lead-form"
      data-testid="lead-form-section"
      className="relative py-24 sm:py-32 bg-[#F9FAFB]"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#0A1936]" />
              <span className="font-mono uppercase tracking-[0.24em] text-[11px] text-[#0A1936]/70">
                09 · Next Steps
              </span>
            </div>
            <h2 className="font-serif text-[#0A1936] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Start in
              <br />
              <span className="italic">15 minutes.</span>
            </h2>
            <p className="mt-8 text-lg text-[#0A1936]/70 leading-relaxed max-w-md">
              No commitment. No cost. Just clarity on what you&apos;re owed. We&apos;ll
              confirm your import exposure and give you an estimated recovery
              figure.
            </p>

            <ul className="mt-10 space-y-3 text-sm text-[#0A1936]/80">
              {[
                "Free 15-min assessment",
                "No obligation, no cost",
                "Estimated recovery figure",
                "Response within 1 business day",
              ].map((t) => (
                <li key={t} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F5B800]" />
                  {t}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <form
              onSubmit={onSubmit}
              data-testid="lead-form"
              className="relative bg-white border border-[#0A1936]/10 rounded-md p-8 lg:p-10 shadow-[0_20px_60px_-20px_rgba(10,25,54,0.15)]"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Field
                  label="Name*"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  testId="lead-form-name"
                />
                <Field
                  label="Company"
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  testId="lead-form-company"
                />
                <Field
                  label="Email*"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  testId="lead-form-email"
                />
                <Field
                  label="Phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={onChange}
                  testId="lead-form-phone"
                />
                <div className="sm:col-span-2">
                  <Field
                    label="Estimated 2025 tariffs paid"
                    name="estimated_tariffs"
                    placeholder="e.g. $250,000"
                    value={form.estimated_tariffs}
                    onChange={onChange}
                    testId="lead-form-tariffs"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block font-mono uppercase tracking-[0.2em] text-[10px] text-[#0A1936]/60 mb-2">
                    Anything else? (optional)
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    value={form.message}
                    onChange={onChange}
                    data-testid="lead-form-message"
                    className="w-full bg-transparent border-b border-[#0A1936]/20 focus:border-[#F5B800] py-3 text-[#0A1936] placeholder-[#0A1936]/30 transition-colors outline-none resize-none"
                    placeholder="Sectors, countries, timing..."
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="lead-form-submit"
                className="btn-magnetic mt-10 w-full inline-flex items-center justify-center gap-3 bg-[#0A1936] hover:bg-[#1B2F55] disabled:opacity-70 text-white font-semibold text-base px-8 py-5 rounded-sm transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Request Free Assessment
                    <ArrowUpRight size={18} />
                  </>
                )}
              </button>

              {submitted && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  data-testid="lead-form-success"
                  className="mt-6 p-4 border border-[#F5B800] bg-[#F5B800]/10 rounded-sm text-[#0A1936] text-sm"
                >
                  Received. Our team will reach out within one business day.
                </motion.div>
              )}

              <p className="mt-5 text-[11px] text-[#0A1936]/50">
                By submitting, you agree to be contacted about IEEPA tariff
                recovery. We never share your data.
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Field({
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder,
  testId,
}) {
  return (
    <div>
      <label className="block font-mono uppercase tracking-[0.2em] text-[10px] text-[#0A1936]/60 mb-2">
        {label}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full bg-transparent border-b border-[#0A1936]/20 focus:border-[#F5B800] py-3 text-[#0A1936] placeholder-[#0A1936]/30 transition-colors outline-none"
      />
    </div>
  );
}
